<!-- 
 挖空卡片组件，这个组件不应该直接使用，而是应该使用 CardEssence
 支持打字机效果
-->
<template>
  <div
    v-if="parsed"
    :class="[
      'flex items-start gap-2',
      {
        'flex-col': !_global.isPcMode,
      },
    ]"
  >
    <TextClamp class="w-full">
      <Content
        :content="typewriting ? typewriterContent! : parsedContent"
        :single-line="singleLine"
      >
        <template #text="{ node }">
          <Text
            class="content"
            :node="node"
            :show-dot-style="false"
          ></Text>
        </template>

        <template #cloze="{ node }">
          <span class="font-bold g-cloze">
            {{ node.text }}
          </span>
        </template>
      </Content>
    </TextClamp>

    <Image
      v-if="card.illustration && typewriting"
      preview
      :class="[
        'illustration shrink-0',
        {
          'ml-auto': _global.isPcMode,
        },
      ]"
    >
      <template #image>
        <img
          :src="_global.assetUrl(card.illustration)"
          class="h-78px w-78px rounded-4px object-cover"
          @click.stop
        />
      </template>
      <template #preview="slotProps">
        <img
          :src="_global.assetUrl(card.illustration)"
          class="object-cover"
          :style="slotProps.style"
          @click="slotProps.onClick"
        />
      </template>
    </Image>
  </div>
  <div v-else>
    {{ _t('cardview.parse_failed') }}
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { parseClozeCard } from '@/utils/card'
import Text from '../card-render/Text.vue'
import Content from '../card-render/Content.vue'
import Image from 'primevue/image'

import type { ClozeCard } from '@/types/core'
import type { Content as ContentType } from '@/types/model'
import TextClamp from '@/components/TextClamp.vue'

const props = defineProps<{
  /** 卡片数据 */
  card: ClozeCard
  /** 是否启用打字机效果 */
  enableTypewriter?: boolean
  /** 是否限制为单行显示 */
  singleLine?: boolean
}>()

const emit = defineEmits<{
  /** 打字机效果结束 */
  (e: 'typewriter-complete'): void
}>()

defineExpose({ runTypewriter, clearTypewriter })

const parsed = computed(() => parseClozeCard(props.card))
const parsedContent = computed(() => parsed.value.card.content)

const typewriting = computed(
  () => typewriterContent.value && typewriterContent.value.length > 0
)

const typewriterContent = ref<ContentType | null>(null)

// 获取随机波动的速度值
function getRandomizedSpeed(minSpeed = 30, maxSpeed = 100): number {
  if (minSpeed == maxSpeed) {
    return minSpeed
  }
  // 在最小和最大速度之间随机
  return Math.floor(minSpeed + Math.random() * (maxSpeed - minSpeed + 1))
}

let typewriterTimer: number | null = null
function clearTypewriterTimer() {
  if (typewriterTimer) {
    clearInterval(typewriterTimer)
    typewriterTimer = null
  }
}

function clearTypewriter() {
  typewriterContent.value = [] as ContentType
  clearTypewriterTimer()
}

function runTypewriter() {
  // 初始化
  clearTypewriter()
  let blockIndex = 0
  let inlineIndex = 0

  // 如果内容为空，直接退出
  if (!parsedContent.value) return

  // 使用setTimeout实现带随机速度的打字效果
  function typeNextChar() {
    if (typewriterContent.value == null) {
      return
    }
    // 确认下一个要显示的位置
    while (true) {
      if (blockIndex >= typewriterContent.value.length) {
        // 如果 blockIndex 超出 BlockNode 列表长度
        if (blockIndex < parsedContent.value.length) {
          // 如果原始数据中还有未显示的 BlockNode，取下一个显示
          typewriterContent.value.push({
            type: 'p',
            content: [],
          })
          inlineIndex = 0
        } else {
          // 此时已经全部显示完
          emit('typewriter-complete')
          typewriterContent.value = null
          return
        }
      } else if (
        inlineIndex >= typewriterContent.value[blockIndex].content.length
      ) {
        // 如果 inlineIndex 超出 当前 BlockNode 的 InlineNode 列表长度
        if (inlineIndex < parsedContent.value[blockIndex].content.length) {
          // 如果原始数据中当前 BlockNode 还有未显示的 InlineNode，取下一个显示
          typewriterContent.value[blockIndex].content.push({
            ...parsedContent.value[blockIndex].content[inlineIndex],
            text: '',
          })
        } else {
          // 当前 BlockNode 全部显示完，进入下一个
          blockIndex += 1
        }
      } else if (
        typewriterContent.value[blockIndex].content[inlineIndex].text.length ==
        parsedContent.value[blockIndex].content[inlineIndex].text.length
      ) {
        // 文本等长，说明当前 InlineBlock 显示完毕，进入下一个
        inlineIndex += 1
      } else {
        break
      }
    }
    // 显示下一个字
    let textIndex =
      typewriterContent.value[blockIndex].content[inlineIndex].text.length
    typewriterContent.value[blockIndex].content[inlineIndex].text +=
      parsedContent.value[blockIndex].content[inlineIndex].text[textIndex]

    // 使用随机速度安排下一次执行
    typewriterTimer = window.setTimeout(typeNextChar, getRandomizedSpeed())
  }

  // 开始执行打字效果
  typeNextChar()
}

// 初始化
onMounted(() => {
  if (props.enableTypewriter) {
    // 如果开启打字机效果且应当立即开始
    runTypewriter()
  }
})

// 监听 enableTypewriter，重启或停止打字机
// 这个功能是为了方便调试，实际使用中不建议修改 enableTypewriter 的值
watch(
  () => props.enableTypewriter,
  newValue => {
    if (newValue === true) {
      runTypewriter()
    } else {
      clearTypewriter()
    }
  }
)

// 组件卸载前清除定时器
onBeforeUnmount(() => {
  clearTypewriterTimer()
})
</script>
<style scoped>
.illustration :deep(.p-image-preview-indicator) {
  opacity: 0;
}
</style>
